<template>
  <div>
    <!-- Um unico destino: botao direto (espelha o navigation do Syncou). -->
    <md-button
      v-if="singleEntry"
      class="noMargin md-raised md-primary"
      :disabled="editable"
      @click.prevent="() => go(singleEntry)"
    >
      {{ singleEntry.label }}
    </md-button>

    <!-- Varios destinos: menu agrupado por tipo. -->
    <md-menu v-else-if="items.length" md-size="medium" md-align-trigger>
      <md-button
        class="md-raised md-primary"
        :disabled="editable"
        md-menu-trigger
      >
        Navegar
        <md-icon>arrow_drop_down</md-icon>
      </md-button>

      <md-menu-content>
        <template v-for="row in menuRows">
          <md-subheader v-if="row.kind === 'header'" :key="row.key">
            {{ row.title }}
          </md-subheader>
          <md-menu-item v-else :key="row.key" @click="() => go(row.item)">
            {{ row.item.label }}
          </md-menu-item>
        </template>
      </md-menu-content>
    </md-menu>
  </div>
</template>

<script>
import { EventBus, Topics, Types } from '@/event-bus'
import { isSafeHttpUrl } from '@/utils/validations/url'

export default {
  name: 'ViewShowNavigation',
  props: {
    // Usado para navegar entre views/equipamentos do mesmo local; cada entrada
    // ja carrega o localId, entao este prop e mantido apenas por consistencia
    // com os demais componentes do dashboard.
    localId: {
      type: String,
      default: null,
    },
    componente: {
      type: Object,
      required: true,
    },
    // No editor (moveable) o clique serve para selecionar/arrastar: navegar
    // trocaria de rota no meio da edicao. Desabilita a navegacao nesse modo.
    editable: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    entries() {
      return (this.componente.value && this.componente.value.entries) || []
    },
    items() {
      return this.entries.map((e, i) => this.toItem(e, i)).filter(Boolean)
    },
    singleEntry() {
      return this.items.length === 1 ? this.items[0] : null
    },
    groups() {
      const titles = {
        url: 'Links',
        view: 'Views',
        equipamento: 'Equipamentos',
      }
      // Ordem fixa para o menu nao "pular" conforme a ordem das entradas.
      return ['url', 'view', 'equipamento']
        .map((type) => ({
          type,
          title: titles[type],
          items: this.items.filter((item) => item.type === type),
        }))
        .filter((group) => group.items.length)
    },
    // Achata os grupos em linhas (cabecalho + itens) porque md-menu-content
    // espera md-subheader/md-menu-item como filhos diretos, sem wrappers.
    menuRows() {
      const rows = []
      this.groups.forEach((group) => {
        rows.push({
          kind: 'header',
          key: `h-${group.type}`,
          title: group.title,
        })
        group.items.forEach((item) =>
          rows.push({ kind: 'item', key: item.key, item })
        )
      })
      return rows
    },
  },
  methods: {
    toItem(entry, index) {
      if (entry.url) {
        return {
          key: `u-${index}`,
          type: 'url',
          label: entry.url.label || entry.url.url,
          external: true,
          url: entry.url.url,
        }
      }
      if (entry.view) {
        return {
          key: `v-${index}`,
          type: 'view',
          label: entry.view.label || 'View',
          external: false,
          route: {
            name: 'ViewComponentesShow',
            params: {
              localId: String(entry.view.localId),
              viewId: String(entry.view.viewId),
            },
          },
        }
      }
      if (entry.equipamento) {
        return {
          key: `e-${index}`,
          type: 'equipamento',
          label: entry.equipamento.label || 'Equipamento',
          external: false,
          route: {
            name: 'LocalDetalhe',
            params: { localId: String(entry.equipamento.localId) },
            query: { e: String(entry.equipamento.equipamentoId) },
          },
        }
      }
      return null
    },
    go(item) {
      if (this.editable || !item) return

      if (item.external) {
        // Guarda de esquema: so abre http/https. Bloqueia javascript:/data: que
        // poderiam ter sido persistidos/forjados (defesa em profundidade, alem da
        // validacao no form).
        if (!isSafeHttpUrl(item.url)) {
          EventBus.$emit(Topics.NOTIFICATIONS, {
            message: 'URL de navegação inválida',
            type: Types.WARNING,
          })
          return
        }

        // noopener,noreferrer: a URL e configurada pelo usuario; sem isso a aba
        // aberta recebe window.opener e pode redirecionar a original (tabnabbing).
        window.open(item.url, '_blank', 'noopener,noreferrer')
        return
      }

      this.$router.push(item.route)
    },
  },
}
</script>

<style scoped>
.noMargin {
  margin: 0;
}
</style>
